import React from 'react';
import { Link } from 'react-router-dom';
import { Settings, GraduationCap, Headphones, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Services = () => {
  const { settings } = useSiteSettings();
  const iconMap = {
    Settings,
    GraduationCap,
    Headphones,
    Users
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Nos Services
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Un accompagnement complet de A à Z pour la réussite de votre projet laser.<br />
            Bien plus qu'un simple vendeur, un véritable partenaire.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, idx) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="group relative p-10 rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#F5EBDD' }}>
                      <Icon className="w-10 h-10" style={{ color: '#5C3A21' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-4" style={{ color: '#5C3A21' }}>
                        {service.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed text-lg">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Service Breakdown */}
      <section className="py-24" style={{ backgroundColor: '#F5EBDD' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#5C3A21' }}>
              Notre processus d'accompagnement
            </h2>
            <p className="text-xl text-gray-700">
              De votre premier contact à la pleine maîtrise de votre machine
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              {[
                {
                  number: '01',
                  title: 'Analyse de vos besoins',
                  description: 'Nous prenons le temps de comprendre votre projet, vos matériaux, vos volumes de production et votre budget. Un conseil personnalisé pour choisir LA machine adaptée.'
                },
                {
                  number: '02',
                  title: 'Démonstration gratuite',
                  description: 'Voyez la machine en action ! Nous organisons une démonstration sur vos propres matériaux si possible. Aucun engagement, juste pour vous rassurer.'
                },
                {
                  number: '03',
                  title: 'Devis transparent',
                  description: 'Devis détaillé incluant la machine, installation, formation, garantie et SAV. Pas de surprise, tout est clair dès le départ.'
                }
              ].map((step) => (
                <div key={step.number} className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-2xl font-bold" style={{ backgroundColor: '#5C3A21' }}>
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#5C3A21' }}>
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-8">
              {[
                {
                  number: '04',
                  title: 'Livraison et installation',
                  description: 'Installation professionnelle de votre machine. Configuration complète, tests, calibrage. Vous n\'avez rien à faire, on s\'occupe de tout.'
                },
                {
                  number: '05',
                  title: 'Formation sur-mesure',
                  description: 'Formation complète en présentiel : prise en main machine, logiciel, maintenance de premier niveau, optimisation des réglages. Durée adaptée à vos besoins.'
                },
                {
                  number: '06',
                  title: 'Suivi et SAV continu',
                  description: 'Support technique illimité par téléphone, email ou visio. Assistance rapide en cas de problème. Déplacement possible en cas de besoin. Pièces détachées disponibles. Nous restons à vos côtés.'
                }
              ].map((step) => (
                <div key={step.number} className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-2xl font-bold" style={{ backgroundColor: '#5C3A21' }}>
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#5C3A21' }}>
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Support Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#5C3A21' }}>
                Pourquoi notre accompagnement fait la différence
              </h2>
              <div className="space-y-6">
                {[
                  'Beaucoup de clients viennent après un achat en ligne raté : machine inadaptée, aucune formation, SAV inexistant',
                  'Une machine laser est un investissement important : il faut la bonne machine, le bon conseil',
                  'Sans formation, vous perdez du temps et de l\'argent en essais-erreurs',
                  'Sans SAV réactif, chaque panne = perte de production et stress',
                  'Avec AKM Laser, vous avez un interlocuteur humain disponible et compétent'
                ].map((point, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#5C3A21' }} />
                    <p className="text-gray-700 text-lg leading-relaxed">{point}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={settings.servicesImage}
                alt="Support technique"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à démarrer votre projet laser ?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Contactez-nous pour discuter de vos besoins.<br />
            Conseil gratuit et sans engagement.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white font-semibold shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-lg"
            style={{ color: '#5C3A21' }}
          >
            Demander un devis
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
